import { Component, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

/** Handles dynamic import / chunk load failures (e.g. after deployments). */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, ChunkErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk')
    ) {
      return { hasError: true };
    }
    throw error;
  }

  componentDidCatch(error: Error) {
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk')
    ) {
      const lastReload = sessionStorage.getItem('chunk_error_reload');
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem('chunk_error_reload', now.toString());
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-sl-bg flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-sl-accent" />
          <p className="text-muted-foreground">Updating app...</p>
        </div>
      );
    }
    return this.props.children;
  }
}
